import type { FunctionComponent, MutableRefObject } from 'react';
import { Link } from 'react-router-dom';

import { Field, Form, FormTheme } from '@kanda-libs/ks-component-ts';
import {
  Button,
  CopyContent,
  Loader,
  ModalContainer,
  ModalLayoutCenter,
} from '@kanda-libs/ks-design-library';

import { URLS } from 'components/shared/App/constants';

import {
  COPIED_BUTTON_PROPS,
  COPY_BUTTON_PROPS,
  DESTINATION_SELECT_PROPS,
  DESTINATION_SELECT_VALIDATION,
  USER_MODAL_ID,
} from './constants';
import useUserModal from './useUserModal';

export interface UserModalProps {
  email?: string;
  cid?: string;
  closeCallback?: () => void;
}

const UserModal: FunctionComponent<UserModalProps> = function ({
  email,
  cid,
  closeCallback,
}) {
  const { form, onSubmit, url, isSubmitting, resetUrl } = useUserModal(email);
  return (
    <ModalContainer id={USER_MODAL_ID} opacity={50}>
      {({ handleClose, ref }) => (
        <ModalLayoutCenter
          id="staff-user"
          onClose={() => {
            handleClose();
            resetUrl();
            if (closeCallback) closeCallback();
          }}
          ref={ref as MutableRefObject<HTMLDivElement>}
          className="w-9/10 max-w-4xl"
        >
          <p className="mb-2 text-14-22-em text-neutral-700">User</p>
          <p className="mb-4 text-14-22 text-neutral-700">Email: {email}</p>
          <Link
            onClick={() => handleClose()}
            to={URLS.company.replace(':id', cid || '')}
            className="mr-auto mb-4"
          >
            <Button.Text id="user-go-to-company" label="Go to company" />
          </Link>
          <div className="flex flex-row mb-4">
            <div className="my-auto border-b border-neutral-300 w-full" />
            <p className="text-14-22-em mx-2 text-neutral-600">OR</p>
            <div className="my-auto border-b border-neutral-300 w-full" />
          </div>
          <p className="mb-6 text-14-22-em text-neutral-700">
            Create login link
          </p>
          <FormTheme variant="streamline">
            <Form form={form} onSubmit={onSubmit} id="staff-user-db-login">
              <div className="flex flex-row gap-4">
                <Field.Validator validation={DESTINATION_SELECT_VALIDATION}>
                  <Field.Select {...DESTINATION_SELECT_PROPS} />
                </Field.Validator>
                <Button.Text
                  size={32}
                  submit
                  id="staff-user-db-login-submit"
                  label="Submit"
                  disabled={isSubmitting}
                  className="mt-6"
                />
              </div>
            </Form>
          </FormTheme>
          <Loader isLoading={isSubmitting} />
          <div className="flex flex-row">
            {url && (
              <>
                <p className="p-2 text-14-22 bg-neutral-400 max-w-full break-all rounded-lg mr-4">
                  {url}
                </p>
                <div className="min-w-20 my-auto">
                  <CopyContent
                    content={url}
                    clickedView={
                      <Button.Link {...COPIED_BUTTON_PROPS} size={14} />
                    }
                  >
                    <Button.Link {...COPY_BUTTON_PROPS} size={14} />
                  </CopyContent>
                </div>
              </>
            )}
          </div>
        </ModalLayoutCenter>
      )}
    </ModalContainer>
  );
};

export default UserModal;
